import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './../components/navbar';
import Footer from '../components/footer';
import Hero1 from "../assets/images/5.webp";
import Hero2 from "../assets/images/13.webp";
import Hero3 from "../assets/images/14.webp";
import Hero4 from "../assets/images/16.webp";
import gelery from "../assets/images/gelery.png";

import {
    Building2, Users, Handshake, IndianRupee, ArrowRight,
    Target, Briefcase, Trophy, MapPin, Factory, Cog,
    Calendar, Send, MessageCircle,
    Clock, Heart, Rocket, Activity,
    Phone, ChevronDown, Mail
} from 'lucide-react';

// --- Sub-Component: Home Page Member Card ---
const HomeMemberCard = ({ member, visibilityClass }) => {
    const [imgError, setImgError] = useState(false);

    // Helper to get initials
    const getInitials = (name) => {
        if (!name) return 'MB';
        const parts = name.trim().split(' ');
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    return (
        <div className={`${visibilityClass} group bg-white rounded-[20px] p-6 shadow-[0_2px_15px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.1)] border border-gray-100 transition-all duration-300 flex flex-col items-center h-full hover:-translate-y-1 animate-fade-in-scroll opacity-0 translate-y-5`}>

            {/* Profile Picture / Initials - CENTERED */}
            <div className="w-20 h-20 rounded-full mb-4 overflow-hidden flex items-center justify-center shadow-sm bg-orange-50 mx-auto">
                {!imgError && member.profile_picture ? (
                    <img
                        src={member.profile_picture}
                        alt=""
                        onError={() => setImgError(true)}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    // Initials in ORANGE
                    <span className="text-[#FB9724] text-2xl font-bold">
                        {getInitials(member.full_name)}
                    </span>
                )}
            </div>

            {/* Name */}
            <h4 className="text-lg font-bold text-gray-900 text-center mb-1 line-clamp-1">{member.full_name}</h4>

            {/* Industry - BLUE Color */}
            <p className="text-[#1A87CF] text-center font-medium mb-1 text-sm line-clamp-1">{member.industry}</p>

            {/* Chapter */}
            <p className="text-gray-400 text-xs text-center mb-6">{member.chapter_name || 'General'} Chapter</p>

            {/* Three Small Pill Buttons (Call, Chat, Email) - Small Text, Not Uppercase */}
            <div className="mt-auto grid grid-cols-3 gap-2 w-full">
                {/* Call */}
                <a
                    href={`tel:${member.whatsapp_number}`}
                    className="flex flex-col xl:flex-row items-center justify-center py-2.5 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300"
                    title="Call"
                >
                    <Phone size={16} strokeWidth={2.5} />
                    <span className="hidden xl:block ml-1.5 text-xs font-semibold">Call</span>
                </a>

                {/* Chat */}
                <a
                    href={`https://wa.me/${member.whatsapp_number}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-col xl:flex-row items-center justify-center py-2.5 rounded-xl bg-green-50 text-green-600 hover:bg-green-600 hover:text-white transition-all duration-300"
                    title="WhatsApp"
                >
                    <MessageCircle size={16} strokeWidth={2.5} />
                    <span className="hidden xl:block ml-1.5 text-xs font-semibold">Chat</span>
                </a>

                {/* Email */}
                <a
                    href={`mailto:${member.email}`}
                    className="flex flex-col xl:flex-row items-center justify-center py-2.5 rounded-xl bg-orange-50 text-orange-600 hover:bg-orange-500 hover:text-white transition-all duration-300"
                    title="Email"
                >
                    <Mail size={16} strokeWidth={2.5} />
                    <span className="hidden xl:block ml-1.5 text-xs font-semibold">Email</span>
                </a>
            </div>
        </div>
    );
};

const Home = () => {
    const navigate = useNavigate();

    // --- State Management ---
    const [currentSlide, setCurrentSlide] = useState(0);
    const [activeFAQ, setActiveFAQ] = useState(null);

    // API Base URL
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    // --- Stats / Total Data State ---
    const [counts, setCounts] = useState({
        cities: 0,
        members: 0,
        meetings: 0,
        business: 0
    });
    // Store targets fetched from API
    const [targets, setTargets] = useState({
        cities: 3,
        members: 125,
        meetings: 966,
        business: 10
    });
    const [hasAnimatedCounts, setHasAnimatedCounts] = useState(false);
    const [isStatsDataLoaded, setIsStatsDataLoaded] = useState(false);

    // --- Members Data State ---
    const [membersData, setMembersData] = useState([]);
    const [totalMemberCount, setTotalMemberCount] = useState(0);

    // --- Chapters Data State (NEW) ---
    const [chaptersData, setChaptersData] = useState([]);

    // --- Form States ---
    const [joinFormData, setJoinFormData] = useState({
        fullName: '',
        businessName: '',
        city: '',
        industryCategory: '',
        phoneNumber: '',
        email: '',
        businessDescription: ''
    });

    const [contactFormData, setContactFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    // Refs
    const statsRef = useRef(null);

    // --- Content Data ---
    // --- Content Data ---
    const [slides, setSlides] = useState([]);

    useEffect(() => {
        const fetchHeroSlides = async () => {
            try {
                // Aapka API URL environment variable se lega
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/landing-page/hero-section`);
                const result = await response.json();

                if (result.success && Array.isArray(result.data)) {
                    const formattedSlides = result.data.map((item) => ({
                        _id: item._id,
                        image: item.hero_image,
                        title: item.heading,
                        desc: item.subHeading
                    }));
                    setSlides(formattedSlides);
                }
            } catch (error) {
                console.error("Error fetching slides:", error);
            }
        };

        fetchHeroSlides();
    }, []);

    // --- API: Fetch Total Data ---
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/total-data`);
                const result = await response.json();
                if (result.success && result.data) {
                    setTargets({
                        cities: Math.floor(result.data.totalCities),
                        members: Math.floor(result.data.totalMember),
                        meetings: Math.floor(result.data.totalPersonalMeetings),
                        business: Math.floor(result.data.totalBusiness)
                    });
                    setIsStatsDataLoaded(true);
                }
            } catch (error) {
                console.error("Error fetching stats:", error);
                setIsStatsDataLoaded(true);
            }
        };
        fetchStats();
    }, []);

    // --- API: Fetch Members ---
    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/get-our-member?role=member`);
                const result = await response.json();
                if (result.success) {
                    setMembersData(result.data);
                    setTotalMemberCount(result.count);
                }
            } catch (error) {
                console.error("Error fetching members:", error);
            }
        };
        fetchMembers();
    }, []);

    // --- API: Fetch Networks/Chapters (NEW) ---
    useEffect(() => {
        const fetchChapters = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/get-all-networks`);
                const result = await response.json();
                if (result.success) {
                    setChaptersData(result.data);
                }
            } catch (error) {
                console.error("Error fetching chapters:", error);
            }
        };
        fetchChapters();
    }, []);

    // --- Helper: Format Number separate from Unit ---
    const getFormattedStat = (num) => {
        if (!num) return { val: 0, unit: '' };
        if (num >= 10000000) return { val: (num / 10000000).toFixed(2).replace(/\.00$/, ''), unit: 'Cr+' };
        if (num >= 100000) return { val: (num / 100000).toFixed(2).replace(/\.00$/, ''), unit: 'L+' };
        if (num >= 10000) return { val: (num / 1000).toFixed(1).replace(/\.0$/, ''), unit: 'K+' };
        return { val: num.toLocaleString('en-IN'), unit: '' };
    };

    // --- Helper: Get Chapter Theme Based on City (To maintain design consistency) ---
    const getChapterTheme = (cityName) => {
        const name = cityName?.toLowerCase() || '';
        if (name.includes('ahmedabad')) {
            return {
                themeColor: '#FB9724',
                borderColor: 'border-orange-200',
                bgColor: 'bg-orange-50',
                Icon: Building2
            };
        } else if (name.includes('surat')) {
            return {
                themeColor: '#1A87CF',
                borderColor: 'border-blue-200',
                bgColor: 'bg-blue-50',
                Icon: Factory
            };
        } else {
            return {
                themeColor: 'black',
                borderColor: 'border-gray-200',
                bgColor: 'bg-gray-50',
                Icon: Cog
            };
        }
    };

    // --- Effects: Slider ---
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [slides.length]);

    // --- Effects: Stats Animation ---
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !hasAnimatedCounts && isStatsDataLoaded) {
                    setHasAnimatedCounts(true);
                    const duration = 2000;
                    const steps = 50;
                    const intervalTime = duration / steps;
                    let currentStep = 0;
                    const counterInterval = setInterval(() => {
                        currentStep++;
                        const progress = currentStep / steps;
                        setCounts({
                            cities: Math.ceil(targets.cities * progress),
                            members: Math.ceil(targets.members * progress),
                            meetings: Math.ceil(targets.meetings * progress),
                            business: Math.ceil(targets.business * progress)
                        });
                        if (currentStep >= steps) {
                            clearInterval(counterInterval);
                            setCounts(targets);
                        }
                    }, intervalTime);
                }
            },
            { threshold: 0.5 }
        );
        if (statsRef.current) observer.observe(statsRef.current);
        return () => observer.disconnect();
    }, [hasAnimatedCounts, isStatsDataLoaded, targets]);

    // --- Effects: Scroll Animations ---
    useEffect(() => {
        const handleScroll = () => {
            const elements = document.querySelectorAll('.animate-fade-in-scroll');
            elements.forEach((element) => {
                const elementTop = element.getBoundingClientRect().top;
                if (elementTop < window.innerHeight - 150) {
                    element.classList.add('opacity-100', 'translate-y-0');
                    element.classList.remove('opacity-0', 'translate-y-5');
                }
            });
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // --- Helpers ---
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const headerHeight = 90;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
    };

    const toggleFAQ = (index) => setActiveFAQ(activeFAQ === index ? null : index);

    // --- Form Handlers ---
    const handleJoinChange = (e) => {
        setJoinFormData({ ...joinFormData, [e.target.name]: e.target.value });
    };

    const handleJoinSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_BASE_URL}/applications`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(joinFormData)
            });
            const result = await response.json();
            if (result.success) {
                alert(result.message || 'Application submitted successfully!');
                setJoinFormData({
                    fullName: '', businessName: '', city: '', industryCategory: '',
                    phoneNumber: '', email: '', businessDescription: ''
                });
            } else {
                alert('Failed to submit application. Please try again.');
            }
        } catch (error) {
            console.error('Join Error:', error);
            alert('An error occurred. Please try again later.');
        }
    };

    const handleContactChange = (e) => {
        setContactFormData({ ...contactFormData, [e.target.name]: e.target.value });
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_BASE_URL}/contact-us`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(contactFormData)
            });
            const result = await response.json();
            if (result.success) {
                alert(result.message || 'Message sent successfully!');
                setContactFormData({ name: '', email: '', subject: '', message: '' });
            } else {
                alert('Failed to send message. Please try again.');
            }
        } catch (error) {
            console.error('Contact Error:', error);
            alert('An error occurred. Please try again later.');
        }
    };

    // --- Styles ---
    const btnPrimaryClass = `bg-[#FB9724] text-white border-none hover:bg-[#E0851F] hover:shadow-[0_5px_15px_rgba(251,151,36,0.3)] transition-all duration-300`;
    const btnSecondaryClass = `bg-[#1A87CF] text-white border-none hover:bg-[#1569A3] hover:shadow-[0_5px_15px_rgba(26,135,207,0.3)] transition-all duration-300`;
    const btnOutlineClass = `border-2 border-[#1A87CF] text-[#1A87CF] bg-transparent hover:bg-[#1A87CF] hover:text-white transition-all duration-300`;
    const cardHoverClass = `transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.1)]`;

    // ----------------------------------------------------------------------
    //  ACTIVITY LOGIC (UPDATED API INTEGRATION)
    // ----------------------------------------------------------------------
    const [activities, setActivities] = useState([]);
    const [currentActivityIdx, setCurrentActivityIdx] = useState(0);
    const [currentImageIdx, setCurrentImageIdx] = useState(0);
    const [sliderOpacity, setSliderOpacity] = useState(1);

    // 1. Fetch Activities from API
    useEffect(() => {
        const fetchActivities = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/get-all-activities`);
                const result = await response.json();

                if (result.success && result.data) {
                    const validActivities = result.data.filter(item => item.isActive);
                    setActivities(validActivities);
                }
            } catch (error) {
                console.error("Error fetching activities:", error);
            }
        };
        fetchActivities();
    }, []);

    // 2. Carousel Logic (Dynamic based on API data)
    useEffect(() => {
        if (activities.length === 0) return; // Wait for data

        const slideDuration = 2200;
        const timer = setTimeout(() => {

            const currentActivity = activities[currentActivityIdx];
            const totalImages = currentActivity.images ? currentActivity.images.length : 0;

            // If we reached the last image of the current activity OR the activity has no images
            if (totalImages === 0 || currentImageIdx === totalImages - 1) {
                setSliderOpacity(0); // Fade out
                setTimeout(() => {
                    // Switch to next activity
                    setCurrentActivityIdx((prev) => (prev + 1) % activities.length);
                    setCurrentImageIdx(0); // Reset image index

                    setTimeout(() => {
                        setSliderOpacity(1); // Fade in
                    }, 100);
                }, 300);
            } else {
                // Show next image in the same activity
                setCurrentImageIdx((prev) => prev + 1);
            }
        }, slideDuration);

        return () => clearTimeout(timer);
    }, [currentImageIdx, currentActivityIdx, activities]);

    const currentActivity = activities.length > 0 ? activities[currentActivityIdx] : null;
    const isEven = currentActivityIdx % 2 === 0;

    // ----------------------------------------------------------------------

    return (
        <div className="font-sans text-gray-900 bg-white overflow-x-hidden w-full relative">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Rubik:wght@400;500;600;700;800&display=swap');
        .font-rubik { font-family: 'Rubik', sans-serif; }
        @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-scroll { animation: scroll 20s linear infinite; }
        @keyframes bounce-custom { 
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); } 
            40% { transform: translateY(-10px); } 
            60% { transform: translateY(-5px); } 
        }
        .animate-bounce-custom { animation: bounce-custom 2s infinite; }
        .section-header:after { content: ''; position: absolute; bottom: -10px; left: 0; width: 50%; height: 4px; background: #FB9724; border-radius: 2px; }
        .section-header { position: relative; display: inline-block; }
      `}</style>

            {/* Navbar Integration */}
            <Navbar scrollToSection={scrollToSection} />

            {/* Hero Carousel */}
            <section id="hero" className="relative h-screen w-full overflow-hidden top-0">
                <div className="absolute inset-0 w-full h-full">
                    {slides.map((slide, index) => (
                        <div key={index} className={`absolute w-full h-full transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}>
                            <div className="absolute w-full h-full bg-cover bg-center" style={{ backgroundImage: `url('${slide.image}')` }}></div>
                            <div className="absolute w-full h-full bg-black/50"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center text-white w-[80%] max-w-[800px]">
                                <h1 className="text-3xl sm:text-5xl font-bold mb-4 font-rubik">{slide.title}</h1>
                                <p className="text-xl sm:text-2xl mb-8">{slide.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Hero Content & Stats */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gray-50 pt-20">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-20 h-20 bg-[#FB9724] rounded-full opacity-10 animate-pulse"></div>
                    <div className="absolute top-40 right-20 w-16 h-16 bg-[#1A87CF] rounded-full opacity-10 animate-bounce"></div>
                    <div className="absolute bottom-20 left-20 w-24 h-24 bg-[#1A87CF] rounded-full opacity-10 animate-pulse"></div>
                    <div className="absolute bottom-40 right-10 w-12 h-12 bg-[#FB9724] rounded-full opacity-10 animate-bounce"></div>
                </div>

                <div className="container mx-auto px-4 sm:px-6 text-center relative z-10 pt-20">
                    <div className="max-w-5xl mx-auto">
                        <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-[#FB9724] rounded-full text-sm font-medium mb-6 mt-[10px] animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 ease-out">
                            <Handshake className="w-4 h-4 mr-2" />
                            Gujarat's Premier Business Networking Community
                        </div>

                        <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 font-rubik animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 ease-out delay-100">
                            Connect. Grow.
                            <span className="text-[#FB9724]"> Succeed Together</span>
                            <br />with ABN
                        </h1>

                        <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 ease-out delay-200">
                            ABN is a supportive and inspirational non-profit business group that connects {counts.members}+ business owners
                            across Ahmedabad, Surat, and Rajkot. We help members grow by sharing business references, learning
                            together, and building strong relationships.
                        </p>

                        <div ref={statsRef} className="bg-white/90 backdrop-blur-md rounded-2xl p-6 mb-8 shadow-lg animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 ease-out delay-300">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
                                {[
                                    { Icon: Building2, count: counts.cities, label: 'Cities' },
                                    { Icon: Users, count: counts.members, label: 'Members' },
                                    { Icon: Handshake, count: counts.meetings, label: '1-to-1 Meetings' },
                                    { Icon: IndianRupee, count: counts.business, label: 'Business Done' }
                                ].map((stat, idx) => {
                                    const formatted = getFormattedStat(stat.count);
                                    return (
                                        <div key={idx} className="p-6 transition-all duration-300 hover:-translate-y-1">
                                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#1A87CF]/10 mb-3">
                                                <stat.Icon size={36} className="text-[#1A87CF]" />
                                            </div>
                                            <div className="text-4xl sm:text-5xl font-bold text-[#1A87CF] font-rubik">
                                                {formatted.val}
                                            </div>
                                            <div className="text-lg text-gray-600 mt-2">
                                                {formatted.unit ? `${formatted.unit} ` : ''}{stat.label}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 ease-out delay-500">
                            <button onClick={() => scrollToSection('join')} className={`${btnPrimaryClass} group px-8 py-4 rounded-xl text-lg font-semibold transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center`}>
                                Join ABN Today <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button onClick={() => scrollToSection('contact')} className={`${btnOutlineClass} px-8 py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl`}>
                                Contact Us
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Infinite Tagline Scroll */}
            <section className="relative w-full h-24 bg-gradient-to-r from-orange-50 via-sky-50 to-orange-50 border-t border-b border-gray-200 overflow-hidden">
                <div className="absolute top-0 bottom-0 left-0 w-20 z-10 bg-gradient-to-r from-white via-white/80 to-transparent"></div>
                <div className="absolute top-0 bottom-0 right-0 w-20 z-10 bg-gradient-to-l from-white via-white/80 to-transparent"></div>
                <div className="absolute inset-0 flex items-center">
                    <div className="animate-scroll whitespace-nowrap text-5xl font-black uppercase tracking-wider flex">
                        <span className="inline-flex items-center space-x-16 pr-16">
                            {[1, 2, 3, 4].map((i) => (
                                <React.Fragment key={i}>
                                    <span className="inline-flex items-center space-x-4">
                                        <span className="text-orange-500 drop-shadow-sm">Happy</span>
                                        <span className="text-sky-500 drop-shadow-sm">Networking</span>
                                    </span>
                                    <span className="text-2xl text-gray-400">•</span>
                                </React.Fragment>
                            ))}
                        </span>
                        <span className="inline-flex items-center space-x-16 pr-16">
                            {[1, 2, 3, 4].map((i) => (
                                <React.Fragment key={`dup-${i}`}>
                                    <span className="inline-flex items-center space-x-4">
                                        <span className="text-orange-500 drop-shadow-sm">Happy</span>
                                        <span className="text-sky-500 drop-shadow-sm">Networking</span>
                                    </span>
                                    <span className="text-2xl text-gray-400">•</span>
                                </React.Fragment>
                            ))}
                        </span>
                    </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
            </section>

            {/* About Us Section */}
            <section id="about" className="py-24 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-2">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-rubik section-header">
                                About <span className="text-[#FB9724]">ABN</span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                Building stronger businesses through meaningful connections and collaborative growth.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            <div className="animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 flex justify-center">
                                <div className="relative w-full max-w-[700px] min-h-[420px] lg:min-h-[500px] flex items-center">
                                    <div className="absolute -left-10 top-10 w-72 h-72 bg-orange-100 rounded-full mix-blend-multiply blur-xl opacity-70 animate-blob"></div>
                                    <div className="absolute -right-10 top-10 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply blur-xl opacity-70 animate-blob animation-delay-2000"></div>
                                    <img src={gelery} alt="About ABN" className="relative w-full h-auto object-contain drop-shadow-xl hover:scale-105 transition-transform duration-500" />
                                </div>
                            </div>

                            <div className="animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 delay-200">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-rubik">Our Purpose</h3>
                                <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                                    Founded with the vision of creating a supportive business ecosystem in Gujarat, ABN connects
                                    85+ business professionals across three major cities. We believe that when businesses help
                                    each other grow, the entire community benefits.
                                </p>
                                <div className="bg-blue-50 p-6 rounded-xl border border-blue-200 shadow-sm">
                                    <div className="flex items-start space-x-3">
                                        <Users className="text-[#1A87CF]" size={24} />
                                        <div>
                                            <h4 className="font-bold text-[#1A87CF] mb-1">Non-Profit Organization</h4>
                                            <p className="text-[#1569A3] text-sm leading-relaxed">
                                                We operate as a non-profit, ensuring all resources are directed towards member
                                                growth and community building, not profit.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-16 text-center animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 delay-300">
                            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto">
                                <h3 className="text-2xl font-bold text-gray-900 mb-6 font-rubik section-header">
                                    How We <span className="text-[#FB9724]">Meet</span>
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {[
                                        { id: 1, title: 'Elevator Pitches', desc: '30-second impactful business presentations', icon: Clock, iconBg: 'bg-orange-100', iconColor: 'text-[#FB9724]' },
                                        { id: 2, title: 'Business Asks', desc: 'Sharing specific client or partnership needs', icon: Target, iconBg: 'bg-blue-100', iconColor: 'text-[#1A87CF]' },
                                        { id: 3, title: 'Referral Sharing', desc: 'Exchanging qualified business opportunities', icon: Handshake, iconBg: 'bg-orange-100', iconColor: 'text-[#FB9724]' }
                                    ].map((item) => (
                                        <div key={item.id} className="text-center group">
                                            <div className={`w-16 h-16 ${item.iconBg} ${item.iconColor} rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110`}>
                                                <item.icon size={28} />
                                            </div>
                                            <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                                            <p className="text-gray-600 text-sm">{item.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Chapters Section (DYNAMIC) */}
            <section id="chapters" className="py-24 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16 animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-rubik section-header">
                                Our <span className="text-[#1A87CF]">Chapters</span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                Thriving chapters across Gujarat's major business centers
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {chaptersData.map((chapter, i) => {
                                const styles = getChapterTheme(chapter.city?.name);
                                return (
                                    <div key={chapter._id || i} className={`lg:col-span-1 ${cardHoverClass} bg-white rounded-2xl p-8 shadow-lg mb-8 animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 border ${styles.borderColor}`} style={{ transitionDelay: `${i * 200}ms` }}>
                                        <div className="flex items-center mb-6">
                                            <div className="w-12 h-12 text-white rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: styles.themeColor }}>
                                                <styles.Icon size={24} />
                                            </div>
                                            <div>
                                                <h3 className="text-2xl font-bold text-gray-900 font-rubik">{chapter.city?.name}</h3>
                                                <p className="font-medium" style={{ color: styles.themeColor }}>{chapter.totalChapters} Active Chapters</p>
                                            </div>
                                        </div>
                                        <p className="text-gray-600 mb-4 min-h-[60px] line-clamp-3">{chapter.description}</p>
                                        <div className="space-y-3 mb-6">
                                            <div className="flex items-center text-sm text-gray-600">
                                                <Calendar size={16} className="mr-2" style={{ color: styles.themeColor }} />
                                                <span>Every {chapter.meeting?.day}, {chapter.meeting?.startTime} - {chapter.meeting?.endTime}</span>
                                            </div>
                                            <div className="flex items-center text-sm text-gray-600">
                                                <Users size={16} className="mr-2" style={{ color: styles.themeColor }} />
                                                <span>{chapter.totalActiveMembers}+ Active Members</span>
                                            </div>
                                        </div>
                                        <div className={`${styles.bgColor} p-4 rounded-lg border ${styles.borderColor}`}>
                                            <h4 className="font-semibold mb-2" style={{ color: styles.themeColor }}>{chapter.highlight?.title}</h4>
                                            <p className="text-sm opacity-80" style={{ color: styles.themeColor === 'black' ? '#374151' : styles.themeColor }}>{chapter.highlight?.description}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* Activities Section (DYNAMICALLY UPDATED) */}
            <section id="activities" className="py-24 bg-white">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16 animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-rubik section-header">
                                Our <span className="text-[#FB9724]">Activities</span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                Building relationships through business networking and social engagement
                            </p>
                        </div>

                        {/* HARDCODED CARDS - KEPT AS PER DESIGN REQUEST */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16">
                            <div className="animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                                <div className="flex items-center mb-8">
                                    <div className="w-16 h-16 bg-[#FB9724] text-white rounded-full flex items-center justify-center mr-4">
                                        <Briefcase size={32} />
                                    </div>
                                    <h3 className="text-3xl font-bold text-gray-900 font-rubik">Business Activities</h3>
                                </div>
                                <div className="space-y-6">
                                    <div className={`${cardHoverClass} bg-orange-50 p-6 rounded-xl border border-orange-200`}>
                                        <h4 className="text-xl font-semibold text-[#FB9724] mb-3">Weekly Networking Meetings</h4>
                                        <p className="text-[#E0851F] mb-3">Structured sessions featuring elevator pitches, business asks, and referral sharing</p>
                                        <div className="flex items-center text-sm text-[#FB9724]"><Clock size={16} className="mr-2" /><span>2 hours of focused networking every week</span></div>
                                    </div>
                                    <div className={`${cardHoverClass} bg-blue-50 p-6 rounded-xl border border-blue-200`}>
                                        <h4 className="text-xl font-semibold text-[#1A87CF] mb-3">Business Workshops</h4>
                                        <p className="text-[#1569A3] mb-3">Monthly sessions on sales, marketing, finance, and business growth strategies</p>
                                        <div className="flex items-center text-sm text-[#1A87CF]"><Users size={16} className="mr-2" /><span>Led by industry experts and successful members</span></div>
                                    </div>
                                    <div className={`${cardHoverClass} bg-black p-6 rounded-xl text-white`}>
                                        <h4 className="text-xl font-semibold mb-3">Referral Programs</h4>
                                        <p className="mb-3">Systematic tracking and rewarding of business referrals between members</p>
                                        <div className="flex items-center text-sm"><Target size={16} className="mr-2" /><span>₹10+ crore business generated last year</span></div>
                                    </div>
                                </div>
                            </div>

                            <div className="animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 delay-200">
                                <div className="flex items-center mb-8">
                                    <div className="w-16 h-16 bg-[#1A87CF] text-white rounded-full flex items-center justify-center mr-4">
                                        <Users size={32} />
                                    </div>
                                    <h3 className="text-3xl font-bold text-gray-900 font-rubik">Social Activities</h3>
                                </div>
                                <div className="space-y-6">
                                    <div className={`${cardHoverClass} bg-blue-50 p-6 rounded-xl border border-blue-200`}>
                                        <h4 className="text-xl font-semibold text-[#1A87CF] mb-3">Family Get-togethers</h4>
                                        <p className="text-[#1569A3] mb-3">Quarterly events bringing families together for stronger community bonds</p>
                                        <div className="flex items-center text-sm text-[#1A87CF]"><Heart size={16} className="mr-2" /><span>Building relationships beyond business</span></div>
                                    </div>
                                    <div className={`${cardHoverClass} bg-orange-50 p-6 rounded-xl border border-orange-200`}>
                                        <h4 className="text-xl font-semibold text-[#FB9724] mb-3">Annual Conference</h4>
                                        <p className="text-[#E0851F] mb-3">Flagship event with keynote speakers, awards, and inter-chapter networking</p>
                                        <div className="flex items-center text-sm text-[#FB9724]"><Trophy size={16} className="mr-2" /><span>Celebrating member achievements and milestones</span></div>
                                    </div>
                                    <div className={`${cardHoverClass} bg-gray-100 p-6 rounded-xl border border-gray-200`}>
                                        <h4 className="text-xl font-semibold text-black mb-3">Festival Celebrations</h4>
                                        <p className="text-gray-700 mb-3">Cultural events celebrating festivals and strengthening community spirit</p>
                                        <div className="flex items-center text-sm text-black"><Calendar size={16} className="mr-2" /><span>Honoring traditions while building modern networks</span></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Photo Gallery Preview - DYNAMICALLY RENDERED FROM API */}
                        {currentActivity && (
                            <div className="text-center mb-12 mt-16 animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                                <h3 className="text-3xl font-bold text-gray-900 mb-6 font-rubik section-header">
                                    Activity <span className="text-[#FB9724]">Highlights</span>
                                </h3>
                                <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
                                    Glimpses of our vibrant community, successful events, and impactful moments.
                                </p>

                                <div className="relative bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-gray-100 p-6 md:p-10 max-w-6xl mx-auto transition-opacity duration-300 ease-in-out" style={{ opacity: sliderOpacity }}>
                                    <div className={`flex flex-col md:flex-row items-center gap-8 md:gap-16 transition-all duration-500 ${!isEven ? 'md:flex-row-reverse' : ''}`}>
                                        <div className="w-full md:w-1/2 text-left space-y-6">
                                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 text-[#FB9724] rounded-full font-bold text-xs uppercase tracking-wider">
                                                <Activity size={14} />
                                                <span>{currentActivity.tag}</span>
                                            </div>

                                            <div className="space-y-4">
                                                <h4 className="text-2xl md:text-4xl font-bold text-gray-800 font-rubik leading-tight">
                                                    {currentActivity.title}
                                                </h4>
                                                <p className="text-gray-600 leading-relaxed text-lg">
                                                    {currentActivity.description}
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-2 pt-2">
                                                {activities.map((_, idx) => (
                                                    <div key={idx} className={`transition-all duration-300 rounded-full h-2 ${idx === currentActivityIdx ? 'w-8 bg-[#1A87CF]' : 'w-2 bg-gray-300'}`} />
                                                ))}
                                            </div>
                                        </div>

                                        <div className="w-full md:w-1/2">
                                            <div className="relative aspect-[4/3] w-full">
                                                {currentActivity.images && currentActivity.images.length > 0 ? (
                                                    currentActivity.images.map((img, idx) => {
                                                        let cardClasses = "absolute inset-0 w-full h-full object-cover rounded-2xl shadow-xl border-[6px] border-white transition-all duration-700 cubic-bezier(0.34, 1.56, 0.64, 1)";
                                                        if (idx === currentImageIdx) {
                                                            cardClasses += " opacity-100 translate-x-0 translate-y-0 rotate-0 z-20 scale-100";
                                                        } else if (idx < currentImageIdx) {
                                                            cardClasses += ` opacity-100 -translate-x-2 translate-y-2 z-10 scale-95 brightness-75 ${idx === 0 ? '-rotate-3' : 'rotate-2'}`;
                                                        } else {
                                                            cardClasses += " opacity-0 translate-x-12 -translate-y-4 rotate-6 z-30 scale-105";
                                                        }
                                                        return <img key={idx} src={img} alt={`Activity ${idx}`} className={cardClasses} />;
                                                    })
                                                ) : (
                                                    // Fallback if no images are present in the API for this activity
                                                    <div className="absolute inset-0 w-full h-full bg-gray-200 rounded-2xl flex items-center justify-center text-gray-400">
                                                        No images available
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Members Section (UPDATED) */}
            <section id="members" className="py-24 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16 animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-rubik section-header">
                                Our <span className="text-[#1A87CF]">Members</span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                {totalMemberCount}+ successful business professionals across diverse industries
                            </p>
                        </div>

                        {/* Members Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                            {membersData && membersData.slice(0, 8).map((member, i) => {
                                // Logic: Show first 4 always. Show 5-8 only on medium screens and up (Desktop).
                                const visibilityClass = i < 4 ? 'block' : 'hidden md:block';

                                return (
                                    <HomeMemberCard
                                        key={member._id}
                                        member={member}
                                        visibilityClass={visibilityClass}
                                    />
                                );
                            })}
                        </div>

                        {/* See More Button */}
                        <div className="text-center mb-12">
                            <button
                                onClick={() => navigate('/members')}
                                className={`${btnPrimaryClass} px-8 py-3 rounded-xl text-lg font-semibold flex items-center justify-center mx-auto gap-2`}
                            >
                                See More Members <ArrowRight size={20} />
                            </button>
                        </div>

                        {/* Industry Distribution Chart */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center font-rubik section-header">Industry Distribution</h3>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-orange-100 text-[#FB9724] rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Factory size={24} />
                                    </div>
                                    <div className="font-bold text-gray-900">25%</div>
                                    <div className="text-sm text-gray-600">Manufacturing</div>
                                </div>
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-blue-100 text-[#1A87CF] rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Briefcase size={24} />
                                    </div>
                                    <div className="font-bold text-gray-900">20%</div>
                                    <div className="text-sm text-gray-600">Technology</div>
                                </div>
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-gray-100 text-black rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Handshake size={24} />
                                    </div>
                                    <div className="font-bold text-gray-900">18%</div>
                                    <div className="text-sm text-gray-600">Services</div>
                                </div>
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-orange-100 text-[#FB9724] rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Target size={24} />
                                    </div>
                                    <div className="font-bold text-gray-900">22%</div>
                                    <div className="text-sm text-gray-600">Trading</div>
                                </div>
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-blue-100 text-[#1A87CF] rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Users size={24} />
                                    </div>
                                    <div className="font-bold text-gray-900">15%</div>
                                    <div className="text-sm text-gray-600">Others</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Join Section */}
            <section id="join" className="py-24 bg-[#1A87CF]">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12 animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 font-rubik">
                                Join <span className="text-[#FB9724]">ABN Today</span>
                            </h2>
                            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                                Interested in networking and growing your business with us? Fill the form below and start your journey to business success!
                            </p>
                        </div>
                        <div className="bg-white rounded-2xl p-8 shadow-2xl animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                            <form className="space-y-6" onSubmit={handleJoinSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Full Name *</label>
                                        <input
                                            type="text"
                                            name="fullName"
                                            value={joinFormData.fullName}
                                            onChange={handleJoinChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                            placeholder="Enter your full name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Business Name *</label>
                                        <input
                                            type="text"
                                            name="businessName"
                                            value={joinFormData.businessName}
                                            onChange={handleJoinChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                            placeholder="Your business name"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">City *</label>
                                        <select
                                            name="city"
                                            value={joinFormData.city}
                                            onChange={handleJoinChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                        >
                                            <option value="">Select your city</option>
                                            <option value="ahmedabad">Ahmedabad</option>
                                            <option value="surat">Surat</option>
                                            <option value="rajkot">Rajkot</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Industry Category *</label>
                                        <select
                                            name="industryCategory"
                                            value={joinFormData.industryCategory}
                                            onChange={handleJoinChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                        >
                                            <option value="">Select category</option>
                                            <option value="Manufacturing">Manufacturing</option>
                                            <option value="Services">Services</option>
                                            <option value="Trading">Trading</option>
                                            <option value="Technology">Technology</option>
                                            <option value="Healthcare">Healthcare</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Phone Number *</label>
                                        <input
                                            type="tel"
                                            name="phoneNumber"
                                            value={joinFormData.phoneNumber}
                                            onChange={handleJoinChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                            placeholder="+91 98xxx xxxxx"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Email Address *</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={joinFormData.email}
                                            onChange={handleJoinChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">Tell us about your business</label>
                                    <textarea
                                        name="businessDescription"
                                        value={joinFormData.businessDescription}
                                        onChange={handleJoinChange}
                                        rows="4"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                        placeholder="Brief description of your business, services, and what you're looking for from ABN..."
                                    ></textarea>
                                </div>
                                <div className="text-center">
                                    <button type="submit" className={`${btnSecondaryClass} px-12 py-4 rounded-xl text-lg font-semibold transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center mx-auto gap-2`}>
                                        Submit Application <Send size={20} />
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="mt-12 text-center animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                <h3 className="text-xl font-bold text-white mb-4 font-rubik">What happens next?</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-blue-100">
                                    <div>
                                        <div className="w-12 h-12 bg-white/20 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                                            <span className="font-bold">1</span>
                                        </div>
                                        <div className="font-medium">Application Review</div>
                                        <div className="text-sm">Within 48 hours</div>
                                    </div>
                                    <div>
                                        <div className="w-12 h-12 bg-white/20 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                                            <span className="font-bold">2</span>
                                        </div>
                                        <div className="font-medium">Interview Call</div>
                                        <div className="text-sm">Getting to know you</div>
                                    </div>
                                    <div>
                                        <div className="w-12 h-12 bg-white/20 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                                            <span className="font-bold">3</span>
                                        </div>
                                        <div className="font-medium">Welcome to ABN</div>
                                        <div className="text-sm">Start networking!</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-24 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
                    <div className="mb-16 text-center">
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 font-rubik section-header">
                            Commonly Asked <span className="text-[#FB9724]">Questions</span>
                        </h2>
                    </div>
                    <div className="flex flex-col lg:flex-row gap-12 items-start">
                        <div className="flex-1 space-y-4">
                            {[
                                { q: 'What is ABN (Ahir Business Network)?', a: 'ABN is a non-profit business networking group that connects business owners across Ahmedabad, Surat, and Rajkot. We help members grow through referrals, knowledge sharing, and strong professional relationships.', color: 'orange', borderColor: 'border-orange-200' },
                                { q: 'What does ABN do?', a: 'ABN organizes regular business meetings, learning sessions, and social events to facilitate networking among members. We create opportunities for business referrals, collaborations, and knowledge exchange.', color: 'blue', borderColor: 'border-blue-200' },
                                { q: 'How do I benefit from ABN?', a: 'Members benefit through business referrals, increased visibility, learning opportunities, and access to a trusted network of entrepreneurs. Last year, members generated ₹10+ crore worth of business through the network.', color: 'black', borderColor: 'border-gray-200' },
                                { q: 'How to join ABN?', a: 'You can join by filling out the membership form on our website or attending one of our meetings as a guest. Membership is open to all business owners who share our values of collaboration and mutual growth.', color: 'blue', borderColor: 'border-blue-200' },
                                { q: 'How do I connect with fellow entrepreneurs in ABN?', a: 'You can connect through our bi-weekly meetings, social events, and our member directory. We also have a WhatsApp group for each chapter where members share opportunities and support each other.', color: 'orange', borderColor: 'border-orange-200' }
                            ].map((item, i) => (
                                <div key={i} className={`${cardHoverClass} bg-white rounded-2xl shadow-lg border ${item.borderColor} overflow-hidden`}>
                                    <button className={`w-full flex items-center justify-between p-6 text-left hover:bg-${item.color}-50 transition-all duration-300`} onClick={() => toggleFAQ(i)}>
                                        <div className="flex items-center space-x-4">
                                            <span className={`w-10 h-10 ${item.color === 'orange' ? 'bg-[#FB9724]' : item.color === 'blue' ? 'bg-[#1A87CF]' : 'bg-black'} text-white rounded-full flex items-center justify-center text-sm font-bold`}>0{i + 1}</span>
                                            <span className="text-lg font-semibold text-gray-900">{item.q}</span>
                                        </div>
                                        <span className={`transition-transform duration-300 ${activeFAQ === i ? 'rotate-180' : ''}`}>
                                            <ChevronDown size={24} className={item.color === 'orange' ? 'text-[#FB9724]' : item.color === 'blue' ? 'text-[#1A87CF]' : 'text-black'} />
                                        </span>
                                    </button>
                                    <div className={`transition-all duration-300 ease-in-out overflow-hidden ${activeFAQ === i ? 'max-h-48' : 'max-h-0'}`}>
                                        <div className="p-6 pt-0 border-t border-gray-100">
                                            <p className="text-gray-600 leading-relaxed">{item.a}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex-1 relative">
                            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                                <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                                    alt="Business professionals networking"
                                    className="w-full h-96 lg:h-[500px] object-cover rounded-3xl" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-3xl"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-24 bg-white">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16 animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-rubik section-header">
                                Contact <span className="text-[#FB9724]">Us</span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                Get in touch with us for any queries or to learn more about ABN
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            {/* Contact Form */}
                            <div className="animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000">
                                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-6 font-rubik">Send us a Message</h3>
                                    <form className="space-y-6" onSubmit={handleContactSubmit}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-gray-700 font-medium mb-2">Name</label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={contactFormData.name}
                                                    onChange={handleContactChange}
                                                    required
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                                    placeholder="Your name"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-gray-700 font-medium mb-2">Email</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={contactFormData.email}
                                                    onChange={handleContactChange}
                                                    required
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                                    placeholder="your@email.com"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 font-medium mb-2">Subject</label>
                                            <input
                                                type="text"
                                                name="subject"
                                                value={contactFormData.subject}
                                                onChange={handleContactChange}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                                placeholder="Message subject"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 font-medium mb-2">Message</label>
                                            <textarea
                                                name="message"
                                                value={contactFormData.message}
                                                onChange={handleContactChange}
                                                required
                                                rows="5"
                                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FB9724] outline-none"
                                                placeholder="Your message..."
                                            ></textarea>
                                        </div>
                                        <button type="submit" className={`w-full ${btnPrimaryClass} py-3 px-6 rounded-xl font-semibold shadow-lg`}>
                                            Send Message <Send size={20} className="ml-2 inline" />
                                        </button>
                                    </form>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="animate-fade-in-scroll opacity-0 translate-y-5 transition-all duration-1000 delay-200">
                                {/* Chapter Locations */}
                                <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-gray-200">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-6 font-rubik">Chapter Locations</h3>
                                    <div className="space-y-6">
                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-[#FB9724] text-white rounded-full flex items-center justify-center flex-shrink-0">
                                                <MapPin size={24} />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">Ahmedabad Chapters</h4>
                                                <p className="text-gray-600 text-sm">Multiple locations across the city</p>
                                                <p className="text-[#FB9724] text-sm">Weekly meetings: Thursdays 7:00 AM</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-[#1A87CF] text-white rounded-full flex items-center justify-center flex-shrink-0">
                                                <MapPin size={24} />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">Surat Chapters</h4>
                                                <p className="text-gray-600 text-sm">Business district locations</p>
                                                <p className="text-[#1A87CF] text-sm">Weekly meetings: Tuesdays 7:30 AM</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0">
                                                <MapPin size={24} />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">Rajkot Chapter</h4>
                                                <p className="text-gray-600 text-sm">Central business area</p>
                                                <p className="text-black text-sm">Weekly meetings: Fridays 8:00 AM</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer Integration */}
            <Footer scrollToSection={scrollToSection} />

            {/* Floating WhatsApp Button */}
            <a
                href="https://wa.me/919xxxxxxxxx"
                target="_blank"
                rel="noreferrer"
                className="fixed bottom-6 right-4 sm:bottom-8 sm:right-8 z-[100] animate-bounce-custom"
            >
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-2xl hover:bg-[#128C7E] transition-colors">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                        width="30"
                        height="30"
                        fill="white"
                    >
                        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32 100.5 32 0 132.5 0 256c0 45.1 11.7 89.3 33.9 128.4L0 480l99.7-32.8c37.7 20.6 80.2 31.5 124.2 31.5h.1c123.3 0 223.9-100.5 223.9-224 0-59.3-23.1-115-67-157.6zM224 438.7c-38.5 0-76.3-10.4-109.2-30.2l-7.8-4.6-59.2 19.5 19.8-57.6-5.1-8.1C44.6 322.4 32 289.7 32 256 32 150.1 118.1 64 224 64c50.8 0 98.5 19.8 134.4 55.7 35.9 35.9 55.7 83.6 55.7 134.3 0 105.9-86.1 184.7-190.1 184.7zm101.7-138.1c-5.6-2.8-33.1-16.3-38.3-18.2-5.2-1.9-9-2.8-12.8 2.8-3.7 5.6-14.7 18.2-18 22-3.3 3.7-6.6 4.2-12.2 1.4-33.1-16.5-54.8-29.5-76.7-66.9-5.6-9.6 5.6-8.9 16-29.7 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.8-30.8-17.5-42.1-4.6-11.1-9.3-9.6-12.8-9.8-3.3-.2-7.1-.2-10.9-.2-3.7 0-9.7 1.4-14.7 6.9-5 5.5-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.7 57.4 2.8 3.7 39.1 59.7 94.8 83.8 13.3 5.7 23.7 9.1 31.8 11.6 13.4 4.3 25.6 3.7 35.2 2.3 10.7-1.6 33.1-13.5 37.8-26.5 4.7-13 4.7-24.1 3.3-26.5-1.4-2.4-5.2-3.8-10.8-6.6z" />
                    </svg>
                </div>
            </a>
        </div>
    );
};

export default Home;